import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  format,
  formatDistanceToNow,
  isThisWeek,
  isSameWeek,
  subWeeks,
  parseISO,
  isValid,
} from "date-fns";
import { nb, enUS, pl, sv } from "date-fns/locale";
import {
  DocumentTextIcon,
  CalculatorIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon,
  XCircleIcon,
  TagIcon,
  CurrencyDollarIcon,
  ListBulletIcon,
  FolderIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import { getOfferActivityAsync } from "../../../slices/offer";

/** Keep backward-compatible filters; new categories added by ActivityFeed. */
const FILTERS = [
  "all",
  "kalkyle",
  "linjer",
  "grupper",
  "dokument",
  "revisjoner",
  "import",
  "godkjenning",
  "system",
];

const CATEGORY_ICON = {
  revisjoner: { bg: "#EAF3FD", fg: "#1463C7" },
  kalkyle: { bg: "#F5F3FF", fg: "#7C3AED" },
  import: { bg: "#EEF2FF", fg: "#3730A3" },
  system: { bg: "#F0F9FF", fg: "#0284C7" },
  godkjenning: { bg: "#ECFDF5", fg: "#0F766E" },
  linjer: { bg: "#FFF7ED", fg: "#C2410C" },
  grupper: { bg: "#F0FDF4", fg: "#15803D" },
  dokument: { bg: "#FDF4FF", fg: "#A21CAF" },
};

const ACCENT = "#1463C7";

/** Map action / meta tab hints to offer detail ?tab= values. */
const TAB_ALIASES = {
  kalkyle: "kalkyle",
  calculation: "kalkyle",
  revisjoner: "revisjoner",
  revisions: "revisjoner",
  godkjenning: "godkjenning",
  approval: "godkjenning",
  dokument: "dokument",
  document: "dokument",
  tilbudslinjer: "tilbudslinjer",
  kundevisning: "tilbudslinjer",
  lines: "tilbudslinjer",
  oversikt: "oversikt",
  overview: "oversikt",
  vedlegg: "vedlegg",
  attachments: "vedlegg",
  historikk: "historikk",
  history: "historikk",
  aktiviteter: "historikk",
  activity: "historikk",
  kontroll: "kontroll",
  review: "kontroll",
  sjekkliste: "sjekkliste",
  checklist: "sjekkliste",
};

function parseOccurredAt(value) {
  if (!value) return null;
  const d = typeof value === "string" ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

function dateLocale(i18nLang) {
  if (i18nLang?.startsWith("sv") || i18nLang?.startsWith("se")) return sv;
  if (i18nLang?.startsWith("pl")) return pl;
  if (i18nLang?.startsWith("en")) return enUS;
  return nb;
}

function iconColors(category, eventType) {
  if (eventType === "revision_rejected") {
    return { bg: "#FEF2F2", fg: "#DC2626" };
  }
  if (eventType === "revision_submitted_for_review") {
    return { bg: "#FFFBEB", fg: "#B45309" };
  }
  return CATEGORY_ICON[category] || { bg: "#EAF3FD", fg: ACCENT };
}

function EventIcon({ eventType, category }) {
  const cls = "h-3.5 w-3.5 shrink-0";
  const { fg } = iconColors(category, eventType);
  switch (eventType) {
    case "revision_created":
      return <DocumentTextIcon className={cls} style={{ color: fg }} />;
    case "revision_sent":
      return <PaperAirplaneIcon className={cls} style={{ color: fg }} />;
    case "revision_submitted_for_review":
      return <PaperAirplaneIcon className={cls} style={{ color: fg }} />;
    case "revision_approved":
    case "revision_accepted":
      return <CheckCircleIcon className={cls} style={{ color: fg }} />;
    case "revision_rejected":
      return <XCircleIcon className={cls} style={{ color: fg }} />;
    case "calc_version_created":
      return <CalculatorIcon className={cls} style={{ color: fg }} />;
    case "import_applied":
      return <ArrowDownTrayIcon className={cls} style={{ color: fg }} />;
    case "offer_created":
    case "template_loaded":
      return <DocumentDuplicateIcon className={cls} style={{ color: fg }} />;
    case "currency_changed":
      return <CurrencyDollarIcon className={cls} style={{ color: fg }} />;
    case "line_created":
    case "line_added":
    case "line_updated":
    case "line_changed":
    case "line_deleted":
    case "line_removed":
    case "lines_changed":
    case "lines_generated":
    case "lines_generated_from_calculation":
      return <ListBulletIcon className={cls} style={{ color: fg }} />;
    case "offer_accepted":
      return <CheckCircleIcon className={cls} style={{ color: fg }} />;
    case "group_created":
    case "group_updated":
    case "group_renamed":
    case "group_deleted":
    case "groups_changed":
      return <FolderIcon className={cls} style={{ color: fg }} />;
    case "document_updated":
    case "document_structure_changed":
    case "document_changed":
      return <DocumentIcon className={cls} style={{ color: fg }} />;
    default:
      return <TagIcon className={cls} style={{ color: fg }} />;
  }
}

function buildHeadline(t, event) {
  const meta = event.meta || {};
  const type = event.event_type || "unknown";
  if (type === "calc_version_created" && meta.calculation_title) {
    return t("offers.activity.events.calc_version_created.title_named", {
      title: meta.calculation_title,
      defaultValue: t("offers.activity.events.calc_version_created.title"),
    });
  }
  if (type === "offer_created" && meta.template_title) {
    return t("offers.activity.events.offer_created.title_template", {
      title: meta.template_title,
      defaultValue: t("offers.activity.events.offer_created.title"),
    });
  }
  if (type === "import_applied" && meta.filename) {
    return t("offers.activity.events.import_applied.title_file", {
      filename: meta.filename,
      defaultValue: t("offers.activity.events.import_applied.title"),
    });
  }
  if (type === "revision_approved" && meta.revision_number != null) {
    return t("offers.activity.events.revision_approved.title_named", {
      number: meta.revision_number,
      defaultValue: t("offers.activity.events.revision_approved.title", {
        revision_number: meta.revision_number,
      }),
    });
  }
  if (
    type === "revision_submitted_for_review" &&
    meta.revision_number != null
  ) {
    return t("offers.activity.events.revision_submitted_for_review.title_named", {
      number: meta.revision_number,
      defaultValue: t(
        "offers.activity.events.revision_submitted_for_review.title",
        { revision_number: meta.revision_number }
      ),
    });
  }
  if (type === "currency_changed") {
    return t("offers.activity.events.currency_changed.title", {
      from: meta.from || meta.previous_currency || "—",
      to: meta.to || meta.currency || "—",
      defaultValue: t("offers.activity.events.currency_changed.title"),
    });
  }
  if (
    (type.startsWith("group_") || type.startsWith("line_")) &&
    (meta.title || meta.group_title || meta.line_title)
  ) {
    return t(`offers.activity.events.${type}.title_named`, {
      title: meta.title || meta.group_title || meta.line_title,
      number: meta.number,
      defaultValue: t(`offers.activity.events.${type}.title`, {
        defaultValue: t("offers.activity.events.unknown.title"),
      }),
    });
  }
  if (type === "lines_generated" && meta.lines_count != null) {
    return t("offers.activity.events.lines_generated.title_count", {
      count: meta.lines_count,
      defaultValue: t("offers.activity.events.lines_generated.title"),
    });
  }
  if (type === "lines_changed") {
    const created = meta.created_count ?? 0;
    const updated = meta.updated_count ?? 0;
    const deleted = meta.deleted_count ?? 0;
    return t("offers.activity.events.lines_changed.title", {
      created,
      updated,
      deleted,
      created_count: created,
      updated_count: updated,
      deleted_count: deleted,
      defaultValue: t("offers.activity.events.lines_changed.title"),
    });
  }
  if (type === "groups_changed") {
    const created = meta.created_count ?? 0;
    const updated = meta.updated_count ?? 0;
    const deleted = meta.deleted_count ?? 0;
    return t("offers.activity.events.groups_changed.title", {
      created,
      updated,
      deleted,
      created_count: created,
      updated_count: updated,
      deleted_count: deleted,
      defaultValue: t("offers.activity.events.groups_changed.title"),
    });
  }
  if (type === "template_loaded" && meta.template_title) {
    return t("offers.activity.events.template_loaded.title_named", {
      title: meta.template_title,
      defaultValue: t("offers.activity.events.template_loaded.title"),
    });
  }
  return t(`offers.activity.events.${type}.title`, {
    revision_number: meta.revision_number,
    from: meta.from || meta.previous_currency,
    to: meta.to || meta.currency,
    title: meta.title || meta.group_title || meta.line_title || meta.template_title,
    number: meta.number,
    count: meta.lines_count ?? meta.count,
    created: meta.created_count,
    updated: meta.updated_count,
    deleted: meta.deleted_count,
    defaultValue: t("offers.activity.events.unknown.title"),
  });
}

function buildDescription(t, event) {
  const meta = event.meta || {};
  const type = event.event_type || "unknown";

  if (type === "revision_created" && meta.based_on_revision_number) {
    return t("offers.activity.events.revision_created.description_based", {
      based_on: meta.based_on_revision_number,
      lines: meta.lines_count ?? 0,
      defaultValue: t("offers.activity.events.revision_created.description", {
        revision_number: meta.revision_number,
      }),
    });
  }
  if (type === "calc_version_created" && meta.previous_version_number != null) {
    return t("offers.activity.events.calc_version_created.description_bump", {
      from: meta.previous_version_number,
      to: meta.version_number,
      note: meta.change_note || "",
      defaultValue: t("offers.activity.events.calc_version_created.description"),
    });
  }
  if (type === "import_applied") {
    return t("offers.activity.events.import_applied.description_stats", {
      applied: meta.applied_count ?? 0,
      matched: meta.matched_count ?? 0,
      rows: meta.rows_count ?? 0,
      defaultValue: t("offers.activity.events.import_applied.description"),
    });
  }
  if (type === "offer_created" && meta.template_title) {
    return t("offers.activity.events.offer_created.description_template", {
      title: meta.template_title,
      defaultValue: t("offers.activity.events.offer_created.description"),
    });
  }
  if (type === "revision_approved" && !meta.change_note) {
    return t("offers.activity.events.revision_approved.description_empty");
  }
  if (type === "revision_rejected" && meta.change_note) {
    return t("offers.activity.events.revision_rejected.description_with_note", {
      revision_number: meta.revision_number,
      note: meta.change_note,
    });
  }

  return t(`offers.activity.events.${type}.description`, {
    revision_number: meta.revision_number,
    from: meta.from || meta.previous_currency,
    to: meta.to || meta.currency,
    title: meta.title || meta.group_title || meta.line_title || meta.template_title,
    number: meta.number,
    count: meta.lines_count ?? meta.count,
    created: meta.created_count ?? 0,
    updated: meta.updated_count ?? 0,
    deleted: meta.deleted_count ?? 0,
    created_count: meta.created_count ?? 0,
    updated_count: meta.updated_count ?? 0,
    deleted_count: meta.deleted_count ?? 0,
    template_title: meta.template_title,
    defaultValue: "",
  });
}

function formatRelative(date, locale, t) {
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const dayMs = 24 * 60 * 60 * 1000;
  if (diffMs >= 0 && diffMs < dayMs && now.getDate() === date.getDate()) {
    return t("offers.activity.time.today_at", {
      time: format(date, "HH:mm", { locale }),
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return t("offers.activity.time.yesterday_at", {
      time: format(date, "HH:mm", { locale }),
    });
  }
  return formatDistanceToNow(date, { addSuffix: true, locale });
}

function groupEvents(events) {
  const now = new Date();
  const lastWeekRef = subWeeks(now, 1);
  const groups = { this_week: [], last_week: [], older: [] };

  events.forEach((event) => {
    const d = parseOccurredAt(event.occurred_at);
    if (!d) {
      groups.older.push(event);
      return;
    }
    if (isThisWeek(d, { weekStartsOn: 1 })) {
      groups.this_week.push(event);
    } else if (isSameWeek(d, lastWeekRef, { weekStartsOn: 1 })) {
      groups.last_week.push(event);
    } else {
      groups.older.push(event);
    }
  });

  return [
    { key: "this_week", events: groups.this_week },
    { key: "last_week", events: groups.last_week },
    { key: "older", events: groups.older },
  ].filter((g) => g.events.length > 0);
}

function resolveLinkTab(action, event) {
  const raw =
    action?.linkTab ||
    action?.link_tab ||
    action?.tab ||
    event?.meta?.linkTab ||
    event?.meta?.link_tab ||
    null;
  if (!raw) return null;
  const key = String(raw).toLowerCase();
  return TAB_ALIASES[key] || key;
}

function FilterPill({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-2xl px-[11px] py-[5px] text-xs transition-colors"
      style={
        active
          ? {
              background: ACCENT,
              color: "#fff",
              border: `1px solid ${ACCENT}`,
              fontWeight: 600,
            }
          : {
              background: "#fff",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              fontWeight: 500,
            }
      }
    >
      {label}
    </button>
  );
}

function ActivityCard({ event, t, locale, onAction }) {
  const actorName = event.actor?.name || t("offers.activity.system_actor");
  const occurred = parseOccurredAt(event.occurred_at);
  const headline = buildHeadline(t, event);
  const description = buildDescription(t, event);
  const meta = event.meta || {};
  const actions = event.actions || [];
  const colors = iconColors(event.category, event.event_type);

  const deepLinkTab = resolveLinkTab(
    actions.find((a) => resolveLinkTab(a, event)) || {},
    event
  );

  return (
    <div className="relative flex gap-3 pb-3.5">
      <div
        className="absolute left-[-28px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full"
        style={{ background: colors.bg, color: colors.fg }}
        aria-hidden="true"
      >
        <EventIcon eventType={event.event_type} category={event.category} />
      </div>

      <article className="min-w-0 flex-1 rounded-[9px] border border-slate-200 bg-white px-3 py-2.5">
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0">
            <span className="text-[12.5px] font-[650] text-slate-900">
              {headline}
            </span>
            <div className="mt-px text-[11px] text-slate-400">{actorName}</div>
          </div>
          <span className="shrink-0 whitespace-nowrap text-[11px] text-slate-400">
            {formatRelative(occurred, locale, t)}
          </span>
        </div>

        {description ? (
          <div className="mt-1.5 text-xs text-slate-500">{description}</div>
        ) : null}

        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          {event.event_type === "revision_created" &&
            meta.revision_number != null && (
              <span className="rounded-[10px] bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-600">
                {t("offers.activity.badge.rev", {
                  number: meta.revision_number,
                })}
              </span>
            )}
          {event.event_type === "calc_version_created" &&
            meta.previous_version_number != null && (
              <span className="rounded-[10px] bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-600">
                {t("offers.activity.badge.version", {
                  from: meta.previous_version_number,
                  to: meta.version_number,
                })}
              </span>
            )}
          {event.event_type === "import_applied" && meta.rows_count != null && (
            <span className="rounded-[10px] bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-600">
              {t("offers.activity.badge.matched", {
                matched: meta.matched_count ?? 0,
                rows: meta.rows_count ?? 0,
              })}
            </span>
          )}

          {actions.map((action) => {
            if (action.type === "compare_revisions") {
              return (
                <button
                  key={`${event.id}-compare`}
                  type="button"
                  onClick={() => onAction(action, event)}
                  className="bg-transparent p-0 text-[11.5px] font-semibold hover:underline"
                  style={{ color: ACCENT, border: "none", cursor: "pointer" }}
                >
                  {t("offers.activity.actions.compare_revisions")}
                </button>
              );
            }
            if (action.type === "view_import_log") {
              return (
                <button
                  key={`${event.id}-import`}
                  type="button"
                  onClick={() => onAction(action, event)}
                  className="bg-transparent p-0 text-[11.5px] font-semibold hover:underline"
                  style={{ color: ACCENT, border: "none", cursor: "pointer" }}
                >
                  {t("offers.activity.actions.view_import_log")}
                </button>
              );
            }
            if (action.type === "open_revision") {
              return (
                <button
                  key={`${event.id}-open`}
                  type="button"
                  onClick={() => onAction(action, event)}
                  className="bg-transparent p-0 text-[11.5px] font-semibold hover:underline"
                  style={{ color: ACCENT, border: "none", cursor: "pointer" }}
                >
                  {t("offers.activity.actions.open_revision")}
                </button>
              );
            }
            if (
              action.type === "open_tab" ||
              action.linkTab ||
              action.link_tab ||
              action.tab
            ) {
              const label =
                action.label ||
                action.link_text ||
                t("offers.activity.actions.open_related");
              return (
                <button
                  key={`${event.id}-tab-${action.type || "link"}`}
                  type="button"
                  onClick={() => onAction(action, event)}
                  className="bg-transparent p-0 text-[11.5px] font-semibold hover:underline"
                  style={{ color: ACCENT, border: "none", cursor: "pointer" }}
                >
                  {label}
                </button>
              );
            }
            return null;
          })}

          {/* UI-faithful deep link when meta provides tab but no typed actions */}
          {!actions.length && deepLinkTab ? (
            <button
              type="button"
              onClick={() =>
                onAction({ type: "open_tab", linkTab: deepLinkTab }, event)
              }
              className="bg-transparent p-0 text-[11.5px] font-semibold hover:underline"
              style={{ color: ACCENT, border: "none", cursor: "pointer" }}
            >
              {t("offers.activity.actions.open_related")}
            </button>
          ) : null}
        </div>
      </article>
    </div>
  );
}

/**
 * Claude-aligned Historikk: filter pills + vertical rail + event cards.
 */
export default function OfferActivityTimeline({ offer, refreshKey = 0 }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const locale = dateLocale(i18n.language);

  const { activity, activityOfferId, activityLoading, activityError } =
    useSelector((state) => ({
      activity: state.offers?.activity || [],
      activityOfferId: state.offers?.activityOfferId,
      activityLoading: state.offers?.activityLoading,
      activityError: state.offers?.activityError,
    }));

  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    if (!offer?.id) return;
    dispatch(getOfferActivityAsync({ offerId: offer.id, category: filter }));
  }, [dispatch, offer?.id, filter]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const events = activityOfferId === offer?.id ? activity : [];
  const groups = useMemo(() => groupEvents(events), [events]);

  const navigateTab = useCallback(
    (tab, extra = {}) => {
      if (!tab) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          Object.entries(extra).forEach(([k, v]) => {
            if (v != null && v !== "") next.set(k, String(v));
          });
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const handleAction = (action, event) => {
    if (action.type === "compare_revisions" || action.type === "open_revision") {
      navigateTab("revisjoner", {
        compare: action.type === "compare_revisions" ? "1" : undefined,
        rev: action.revision_id || undefined,
        with:
          action.type === "compare_revisions"
            ? action.compare_with_revision_id
            : undefined,
      });
      return;
    }
    if (action.type === "view_import_log") {
      toast(t("offers.activity.actions.import_coming"));
      navigateTab("kalkyle", {
        import_job: action.import_job_id || undefined,
      });
      return;
    }

    const tab = resolveLinkTab(action, event);
    if (tab) {
      navigateTab(tab);
    }
  };

  if (activityLoading && events.length === 0 && activityOfferId !== offer?.id) {
    return <ResourceLoading />;
  }

  if (activityError && activityOfferId === offer?.id) {
    return (
      <ResourceError
        errors={[
          activityError === "not_authorized"
            ? t("offers.activity.error_unauthorized")
            : t("offers.activity.error"),
        ]}
      />
    );
  }

  return (
    <div className="max-w-[760px] px-5 pb-10 pt-5">
      <div className="mb-[18px] flex flex-wrap gap-2">
        {FILTERS.map((key) => (
          <FilterPill
            key={key}
            active={filter === key}
            label={t(`offers.activity.filters.${key}`)}
            onClick={() => setFilter(key)}
          />
        ))}
      </div>

      {activityLoading && (
        <div className="mb-3 text-sm text-slate-500">
          {t("offers.activity.loading")}
        </div>
      )}

      {!activityLoading && events.length === 0 && (
        <div className="rounded-[9px] border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
          <p className="text-base font-semibold text-slate-800">
            {t("offers.activity.empty_title")}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {t("offers.activity.empty_hint")}
          </p>
        </div>
      )}

      <div className="relative pl-7">
        {groups.length > 0 ? (
          <div
            className="absolute bottom-3 left-[9px] top-1 w-[1.5px] bg-slate-200"
            aria-hidden="true"
          />
        ) : null}

        {groups.map((group) => (
          <section key={group.key}>
            <h2 className="mb-2 -ml-7 pt-3.5 text-[11px] font-[650] uppercase tracking-[0.04em] text-slate-400 first:pt-0">
              {t(`offers.activity.groups.${group.key}`)}
            </h2>
            {group.events.map((event) => (
              <ActivityCard
                key={event.id}
                event={event}
                t={t}
                locale={locale}
                onAction={handleAction}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
